// This is an audio player component that wraps an <audio> element and provides some controls for it,
// but does not handle loading of the audio recording; it must be already provided in src property
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Pause, Trash2 } from 'lucide-react';

import { TranslationsKeys } from 'types/translation';
import { classNameList } from 'utils/property';
import { DateTimeFormat, formatDateTime, formatTime } from 'utils/date';
import { useTranslate } from 'hooks/useTranslate';
import { MessageBar } from 'components/MessageBar';
import { Spinner } from 'components/Spinner';
import { Back15Sec, Fwd15Sec, Play } from './icons';
import styles from './AudioPlayer.module.scss';

interface IProps {
  label?: string;
  createdAt: string;
  src?: string;
  duration: number;
  isLoading: boolean;
  isError: boolean;
  onDelete: () => void;
}

export const AudioPlayer = ({
  label,
  createdAt,
  src,
  duration,
  isLoading,
  isError: isErrorExternal,
  onDelete
}: IProps) => {
  const playerRef = useRef<HTMLAudioElement>(null);
  const [audioExpanded, setAudioExpanded] = useState(false);
  const [playerPosition, setPlayerPosition] = useState(0);
  const [isError, setIsError] = useState(false);
  const { t } = useTranslate();

  useEffect(() => {
    if (audioExpanded && playerRef.current!.src !== src) {
      setIsError(false);
    }
  }, [audioExpanded, src]);

  const formatAudioTime = (time: number) => {
    const h = Math.trunc(time / 3600),
      m = Math.trunc((time % 3600) / 60),
      s = Math.trunc(time % 60);
    const fmt = Intl.NumberFormat('en-US', { minimumIntegerDigits: 2 });
    return `${fmt.format(h)}:${fmt.format(m)}:${fmt.format(s)}`;
  };

  const onCanPlay = () => {
    // Chromium-based browsers record files which are not actually seekable,
    // and duration property on the HTMLAudioElement is Infinity
    // Work around by immediately trying to seek beyond the end and back to the beginning
    if (playerRef.current!.duration === Infinity) {
      const tmpOnTimeUpdate = () => {
        playerRef.current!.currentTime = 0;
        playerRef.current!.removeEventListener('timeupdate', tmpOnTimeUpdate);
      };
      playerRef.current!.addEventListener('timeupdate', tmpOnTimeUpdate);
      playerRef.current!.currentTime = 1000000;
    }
  };

  const onTimeUpdate = () => {
    setPlayerPosition(playerRef.current!.currentTime);
  };

  const onPlay = () => {
    if (playerRef.current!.paused) {
      playerRef.current!.play();
    } else {
      playerRef.current!.pause();
    }
  };

  const onBack15Sec = () => {
    if (playerPosition > 15) {
      playerRef.current!.currentTime = playerPosition - 15;
    } else {
      playerRef.current!.currentTime = 0;
    }
  };

  const onFwd15Sec = () => {
    if (playerPosition < duration - 15) {
      playerRef.current!.currentTime = playerPosition + 15;
    } else {
      playerRef.current!.currentTime = 0;
      playerRef.current!.pause();
    }
  };

  const onSeek = (e: MouseEvent) => {
    const clickX = e.clientX - (e.currentTarget as HTMLDivElement).offsetLeft;
    const seek = (clickX / (e.currentTarget as HTMLDivElement).offsetWidth) * duration;
    playerRef.current!.currentTime = seek;
  };

  return (
    <div className={styles.audio}>
      <audio
        ref={playerRef}
        src={src}
        onCanPlay={onCanPlay}
        onTimeUpdate={onTimeUpdate}
        onError={() => setIsError(true)}
      />
      <button className={styles.audioLabel} onClick={() => setAudioExpanded(!audioExpanded)}>
        <label>{label ?? t(TranslationsKeys.audio_recordingName)}</label>
        {audioExpanded ? <ChevronUp /> : <ChevronDown />}
      </button>
      {createdAt && (
        <div>
          <span>{formatDateTime(createdAt, DateTimeFormat.MEDIUM)}</span>
          <span>{formatTime(createdAt)}</span>
        </div>
      )}
      <div
        className={classNameList(
          styles.audioDetails,
          audioExpanded && styles.expanded,
          (isError || isErrorExternal) && styles.error
        )}>
        <div className={styles.audioSpacer} />
        <div className={styles.audioPosition} onClick={onSeek} role="progressbar">
          <div className={styles.audioKnob} style={{ left: (playerPosition / duration) * 100 + '%' }} />
        </div>
        <div className={styles.audioTimes}>
          <span>{formatAudioTime(playerPosition)}</span>
          <span>{formatAudioTime(duration)}</span>
        </div>
        {isLoading && (
          <div className={styles.audioControls}>
            <Spinner />
          </div>
        )}
        {(isError || isErrorExternal) && (
          <MessageBar
            variant="danger"
            title=""
            description={t(TranslationsKeys.audio_playerError)}
            showCloseAction={false}
          />
        )}
        {!isLoading && (
          <div className={styles.audioControls}>
            <button type="button" onClick={onBack15Sec}>
              <Back15Sec />
            </button>
            <button type="button" onClick={onPlay} className={styles.audioPlayPauseButton}>
              {playerRef.current?.paused ? <Play /> : <Pause />}
            </button>
            <button type="button" onClick={onFwd15Sec}>
              <Fwd15Sec />
            </button>
            <button type="button" className={styles.audioTrashButton} onClick={onDelete}>
              <Trash2 />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
